import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  styled,
} from '@mui/material';
import { JBColors, JBIcon } from '@bjb-gcrm/gcrm-frontend-components';
import { closeLastModal } from 'reducers/modalReducer';
import theme from 'theme';

type SuccessDialogProps =
  | (Omit<DialogProps, 'open'> & {
      successMessage: string;
      children?: React.ReactNode;
      onClose?: () => void;
    })
  | (Omit<DialogProps, 'open'> & {
      onClose?: () => void;
      children: React.ReactNode;
      successMessage?: string;
    });

const DIALOG_PREFIX = 'Dialog';

const dialogClasses = {
  title: `${DIALOG_PREFIX}-title`,
  closeIcon: `${DIALOG_PREFIX}-closeIcon`,
};

const StyledDialog = styled(Dialog)(() => ({
  [`& .MuiDialogContent-root`]: {
    minWidth: '400px',
    margin: `0 ${theme.spacing(2.5)}`,
    padding: `${theme.spacing(2.5)} 0`,
    borderTopColor: JBColors.support.green[130],
    '& > *:not(:first-child)': {
      marginTop: theme.spacing(2),
    },
  },
  [`& .${dialogClasses.title}`]: {
    marginLeft: theme.spacing(1),
    color: JBColors.support.green[130],
    textTransform: 'capitalize',
    lineHeight: 1,
    fontWeight: 300,
  },
  [`& .MuiDialogActions-spacing`]: {
    paddingBottom: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
  },
  [`& .${dialogClasses.closeIcon}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const SuccessDialog: React.FC<SuccessDialogProps> = ({
  successMessage,
  children,
  onClose,
  maxWidth = 'xs',
  ...rest
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onClickOk = () => {
    if (onClose) {
      onClose();
    } else {
      // To close self
      dispatch(closeLastModal());
    }
  };

  return (
    <StyledDialog
      open={true}
      aria-labelledby="success-dialog-title"
      PaperProps={{ square: true }}
      maxWidth={maxWidth}
      {...rest}
    >
      <DialogTitle>
        <Grid container>
          <Grid item xs={8} display="flex">
            <JBIcon iconName="successOutline" size="xl" fill={JBColors.support.green[130]} />
            <Typography variant="h3" color="success" className={dialogClasses.title}>
              {t('modals.successDialog.title')}
            </Typography>
          </Grid>
          <Grid item xs={4} className={dialogClasses.closeIcon}>
            <IconButton onClick={onClickOk} sx={{ padding: 0 }}>
              <JBIcon iconName="close" size="xl" fill={JBColors.support.green[130]} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Typography>{successMessage}</Typography>
        {children}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={onClickOk}>
          {t('common.button.ok')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default SuccessDialog;
